import React, { FunctionComponent, useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { H4, H5, SubHeading } from '../../../design-system/Headings/Headings';
import Spinner from '../../../design-system/Spinner/spinner';
import Card from '../../Card/Card';
import NotificationBar from '../../Notification/NotificationBar';
import { Editor } from '@toast-ui/react-editor';
import Tooltip from '../../../design-system/Tooltip/Tooltip';
import { Button, ToggleSwitch } from '../../../design-system';
import SaveAction from '../../SaveAction/SaveAction';
import ConfirmUnsavedChanges from '../../ConfirmUnsavedChanges/ConfirmUnsavedChanges';
import { AIChatbotProviderValue, useAIChatbot } from './AIChatbotContext';
import useConfirmUnsavedChanges from '../../../hooks/useConfirmUnsavedChanges';
import { aiChatbotStoreKey } from './AIChatbotConstant';
import { PreReleaseTag } from './PreReleaseTag/PreReleaseTag';
import { PublishSDKCard } from './ContextPluginSDKCard';
import { UpgradeCard } from './UpgradeCard';
import { PlatformsProviderValue, usePlatforms } from '../Platforms/PlatformsContext';

export const aiChatbotTitle = 'API Copilot';

const AIChatbot: FunctionComponent = () => {
    const [showModal, setShowModal] = useState(false);
    const [discard, setDiscard] = useState(false);

    const {
        uiState,
        aiChatbotState,
        updateToggleState,
        updateWelcomeMessageState,
        saveData,
        updateContextPluginToggleState,
    } = useAIChatbot() as AIChatbotProviderValue;
    const showContextPlugin = useMemo(() => {
        return aiChatbotState?.contextPlugins?.isAllowedInSubscription;
    }, [aiChatbotState?.contextPlugins?.isAllowedInSubscription]);
    const isCopilotEnabled = aiChatbotState.isAiEnabled;

    useEffect(() => {
        if (!isCopilotEnabled) {
            updateContextPluginToggleState(false);
        }
    }, [aiChatbotState.isAiEnabled]);

    useEffect(() => {
        if (!showContextPlugin && !uiState.isLoading) {
            updateContextPluginToggleState(false);
        }
    }, [showContextPlugin, uiState.isLoading]);

    const { hash } = useLocation();
    const contextPluginsRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!uiState.isLoading && hash === '#context-plugins' && contextPluginsRef.current) {
            contextPluginsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [hash, uiState.isLoading]);

    const editorRef = useRef<Editor | null>(null);

    const _handleOnChange = () => {
        if (editorRef.current) {
            updateWelcomeMessageState(editorRef.current.getInstance().getMarkdown());
        }
    };

    const saveChanges = () => {
        setShowModal(false);
        saveData();
    };

    const discardChanges = () => {
        setShowModal(false);
        setDiscard(true);
    };

    useConfirmUnsavedChanges(
        discard,
        aiChatbotState,
        'apiGroup',
        () => {
            setShowModal(true);
        },
        aiChatbotStoreKey
    );

    if (uiState.isError) return <ErrorView />;
    if (uiState.isLoading) return <LoadingView />;

    return (
        <div className="p-8 pt-0 fade-in-animation">
            <NotificationBar />
            <SaveAction
                isSaving={uiState.isSaving}
                disabled={uiState.isSaving}
                onClick={saveChanges}
            />
            <ConfirmUnsavedChanges
                show={showModal}
                closeModal={setShowModal}
                discardChanges={discardChanges}
                saveChanges={saveChanges}
            />
            <div className="flex mt-10 mb-3">
                <H4>{aiChatbotTitle}</H4>
                <Tooltip
                    message={`Toggle ${aiChatbotTitle}’s visibility on the published portal.`}
                    position="right"
                >
                    <ToggleSwitch
                        onChange={(e) => updateToggleState(e.target.checked)}
                        checked={aiChatbotState.isAiEnabled}
                    />
                </Tooltip>
            </div>
            <SubHeading
                text={`Empower your Developer experience with our hallucination-free ${aiChatbotTitle} – delivering reliable and accurate integration code every time.`}
            ></SubHeading>

            <Card className="mt-10">
                <div>
                    <H5>Welcome message</H5>
                    <SubHeading
                        text={`Create the first message to help ${aiChatbotTitle} users start chatting easily.`}
                    ></SubHeading>
                </div>

                <Editor
                    initialValue={aiChatbotState.welcomeMessage}
                    placeholder={
                        'Here you can add your welcome message. Add inline-code elements in an unordered list. API Copilot will then recognize these as your suggested prompts.'
                    }
                    previewStyle="vertical"
                    height="400px"
                    initialEditType="markdown"
                    useCommandShortcut={true}
                    ref={editorRef}
                    onChange={_handleOnChange}
                    toolbarItems={[
                        ['heading', 'bold', 'italic', 'strike'],
                        ['hr', 'quote'],
                        ['ul', 'ol', 'task', 'indent', 'outdent'],
                        ['link'],
                        ['code', 'codeblock'],
                    ]}
                />
            </Card>

            <>
                <div id="context-plugins" ref={contextPluginsRef} className="flex mt-10 mb-3">
                    <H4>Context Plugins</H4>
                    <PreReleaseTag title="BETA" />
                </div>
                <p className="mb-4 text-goose-800 text-14">
                    Provide deterministic SDK context to AI coding assistants, so they generate
                    accurate, SDK-specific code with fewer hallucinations.&nbsp;
                    <p><b>Coding agents: Cursor, Claude Code and VS Code.</b></p>
                </p>

                {showContextPlugin ? (
                    <>
                        <div className="mt-10">
                            <PublishSDKCard />
                        </div>
                    </>
                ) : (
                    <>
                        <Button
                            text="See Context Plugins in Action"
                            fill="primary"
                            onClick={() =>
                                window.open(
                                    'https://www.apimatic.io/product/context-plugins/showcase?utm_campaign=context_plugins&utm_source=portal_editor#examples',
                                    '_blank',
                                    'noopener,noreferrer'
                                )
                            }
                        />
                        <div className="mt-10">
                            <UpgradeCard />
                        </div>
                    </>
                )}
            </>
        </div>
    );
};

const ErrorView = () => {
    return (
        <div className="flex items-center justify-center w-full h-full">
            <p className="text-base font-bold text-red ">{'Oops! Something went wrong.'}</p>
        </div>
    );
};

const LoadingView = () => {
    return (
        <div className="flex items-center justify-center w-full h-full">
            <Spinner size="xl" />
        </div>
    );
};

export default AIChatbot;
